import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useEffect, useState } from "react";

const IIBBModal = ({
  open,
  onClose,
  provincias,
  retenciones,
  onAdd,
  onUpdate,
  onDelete,
  total,
  formatCurrency,
}) => {
  const [localImportes, setLocalImportes] = useState([]);

  const provinciasUsadas = retenciones
    .map((r) => r.provincia_id)
    .filter(Boolean);

  useEffect(() => {
    setLocalImportes(
      retenciones.map((r) => {
        if (!r.importe) return "";
        return Number(r.importe).toLocaleString("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }),
    );
  }, [retenciones]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Retenciones IIBB por Provincia</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "nowrap",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Agregue las retenciones de Ingresos Brutos desglosadas por provincia
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            sx={{
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            onClick={onAdd}
          >
            Agregar provincia
          </Button>
        </Box>

        {retenciones?.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center", bgcolor: "grey.50" }}>
            <Typography color="text.secondary">
              No hay retenciones IIBB agregadas
            </Typography>
            <Button variant="text" onClick={onAdd} sx={{ mt: 1 }}>
              Agregar la primera retención
            </Button>
          </Paper>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {retenciones?.map((ret, index) => (
              <Box key={index} display="flex" gap={2} alignItems="center">
                <FormControl fullWidth size="small">
                  <Autocomplete
                    value={
                      provincias.find((p) => p.id === ret.provincia_id) || null
                    }
                    onChange={(e, newValue) =>
                      onUpdate(index, "provincia_id", newValue?.id || "")
                    }
                    options={provincias}
                    getOptionLabel={(prov) => prov.provincia}
                    getOptionDisabled={(prov) => {
                      const isUsed = provinciasUsadas.includes(prov.id);
                      const isCurrent = ret.provincia_id === prov.id;
                      return isUsed && !isCurrent;
                    }}
                    renderOption={(props, prov) => {
                      const isUsed = provinciasUsadas.includes(prov.id);
                      const isCurrent = ret.provincia_id === prov.id;
                      return (
                        <li {...props}>
                          {prov.provincia}{" "}
                          {isUsed && !isCurrent ? "(ya seleccionada)" : ""}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Provincia" size="small" />
                    )}
                  />
                </FormControl>

                <TextField
                  size="small"
                  type="text"
                  inputMode="decimal"
                  onWheel={(e) => e.target.blur()}
                  value={localImportes[index] ?? ""}
                  onChange={(e) => {
                    // Solo permite números, coma y punto
                    const raw = e.target.value.replace(/[^0-9,.]/g, "");
                    const newValues = [...localImportes];
                    newValues[index] = raw;
                    setLocalImportes(newValues);
                  }}
                  onBlur={() => {
                    const raw = String(localImportes[index] || "").replace(
                      ",",
                      ".",
                    );
                    const numero = parseFloat(raw) || 0;

                    const newValues = [...localImportes];
                    newValues[index] = numero.toLocaleString("es-AR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    });
                    setLocalImportes(newValues);

                    onUpdate(index, "importe", numero);
                  }}
                  onFocus={(e) => {
                    const raw = String(localImportes[index] || "")
                      .replace(/\./g, "") // saca puntos de miles
                      .replace(",", "."); // coma → punto para editar
                    const newValues = [...localImportes];
                    newValues[index] = raw === "0" ? "" : raw;
                    setLocalImportes(newValues);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 150,
                  }}
                />

                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Divider sx={{ my: 1 }} />

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography fontWeight="600">Total IIBB:</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {formatCurrency(total)}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Guardar y continuar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IIBBModal;
